import { useEffect, useMemo, useState } from 'react';
import { ImportPanel } from './ImportPanel';
import { GameViewer } from './GameViewer';
import { AnalysisPanel } from './AnalysisPanel';
import { getGame, getReport, saveExercises, saveReport, upsertGame } from '../../lib/storage/db';
import { buildExercisesFromReport } from '../../lib/exercises/buildExercises';
import type { CoachingReport, Color, ParsedGame, StoredGame } from '../../types';

export interface GameTabProps {
  onLibraryChanged: () => void;
  /** Bibliothèque déjà chargée par le parent (évite une double lecture IndexedDB) — sert à la liste « Parties récentes ». */
  games: StoredGame[];
  reports: Record<string, CoachingReport>;
  /** Demande d'ouverture externe (ex. depuis Aujourd'hui ou après une synchro Chess.com). */
  openGameId?: string;
  /** Appelé une fois la demande `openGameId` consommée, pour éviter de la reconsommer en boucle. */
  onGameOpened?: () => void;
}

function gameLabel(g: ParsedGame): string {
  return `${g.headers.White ?? '?'} vs ${g.headers.Black ?? '?'}`;
}

export function GameTab({ onLibraryChanged, games, reports, openGameId, onGameOpened }: GameTabProps) {
  const [game, setGame] = useState<ParsedGame | null>(null);
  const [playerColor, setPlayerColor] = useState<Color>('w');
  const [report, setReport] = useState<CoachingReport | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [pendingColorChoice, setPendingColorChoice] = useState(false);

  useEffect(() => {
    if (!game) return;
    let cancelled = false;
    getReport(game.id).then((existing) => {
      if (!cancelled && existing) setReport(existing);
    });
    return () => {
      cancelled = true;
    };
  }, [game]);

  /** Recharge une partie déjà enregistrée (clic sur « Parties récentes », ou demande externe via `openGameId`). */
  async function openStoredGame(id: string) {
    const stored = await getGame(id);
    if (!stored) return;
    const existingReport = await getReport(id);
    const resolvedColor = stored.playerColor ?? existingReport?.playerColor ?? existingReport?.moments[0]?.playerColor;

    setGame(stored.game);
    setReport(existingReport ?? null);
    setSaveNotice(null);
    if (resolvedColor) {
      setPlayerColor(resolvedColor);
      setPendingColorChoice(false);
    } else {
      // Ancienne partie sans couleur enregistrée et sans rapport exploitable :
      // on demande plutôt que de deviner silencieusement (cf. bandeau ci-dessous).
      setPlayerColor('w');
      setPendingColorChoice(true);
    }
  }

  useEffect(() => {
    if (!openGameId) return;
    openStoredGame(openGameId).finally(() => onGameOpened?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGameId]);

  async function handleGameReady(g: ParsedGame, color: Color, pgnRaw: string) {
    setGame(g);
    setPlayerColor(color);
    setPendingColorChoice(false);
    setReport(null);
    try {
      const { inserted } = await upsertGame({
        game: g,
        importedAt: new Date().toISOString(),
        pgnRaw,
        playerColor: color,
        source: g.isDemo ? 'demo' : 'pgn',
      });
      setSaveNotice(inserted ? 'Partie enregistrée localement.' : 'Cette partie était déjà enregistrée (pas de doublon créé).');
      onLibraryChanged();
    } catch {
      setSaveNotice("Impossible d'enregistrer la partie localement (stockage indisponible) — l'analyse reste possible pour cette session.");
    }
  }

  async function confirmColorChoice(color: Color) {
    setPlayerColor(color);
    setPendingColorChoice(false);
    if (!game) return;
    try {
      // On repart de l'enregistrement existant (upsertGame fusionne tel quel :
      // il ne faut surtout pas lui passer un pgnRaw vide, qui écraserait le vrai PGN).
      const existing = await getGame(game.id);
      if (existing) await upsertGame({ ...existing, playerColor: color });
      onLibraryChanged();
    } catch {
      // La couleur reste correcte pour cette session même si l'enregistrement échoue.
    }
  }

  async function handleReportReady(r: CoachingReport) {
    setReport(r);
    if (!game) return;
    try {
      await saveReport(r);
      const exercises = buildExercisesFromReport(game, r);
      if (exercises.length > 0) await saveExercises(exercises);
      onLibraryChanged();
    } catch {
      // L'analyse reste affichée même si la sauvegarde échoue ; l'utilisateur
      // en est informé via le bandeau de stockage dans l'onglet Progression.
    }
  }

  const highlightPlies = useMemo(() => new Set((report?.moments ?? []).map((m) => m.ply)), [report]);

  const recentGames = useMemo(
    () => [...games].sort((a, b) => b.importedAt.localeCompare(a.importedAt)).slice(0, 10),
    [games],
  );

  if (!game) {
    return (
      <div className="game-tab">
        <ImportPanel onGameReady={handleGameReady} />
        {recentGames.length > 0 && (
          <section className="panel recent-games">
            <h2>Parties récentes</h2>
            <ul className="game-candidate-list">
              {recentGames.map((g) => {
                const r = reports[g.game.id];
                return (
                  <li key={g.game.id}>
                    <button type="button" className="game-candidate" onClick={() => openStoredGame(g.game.id)}>
                      <strong>
                        {gameLabel(g.game)}
                        {g.source === 'chesscom' && <span className="badge">Chess.com</span>}
                        {g.game.isDemo && <span className="badge">Démo</span>}
                      </strong>
                      <span className="muted">
                        {g.game.headers.Date ?? 'Date inconnue'} · Résultat {g.game.result} ·{' '}
                        {r ? `${r.moments.length} moment${r.moments.length > 1 ? 's' : ''} clé${r.moments.length > 1 ? 's' : ''}` : 'analyse en attente'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="game-tab">
      <div className="game-tab-header">
        <h2>
          {gameLabel(game)}
          {game.isDemo && <span className="badge">Démo</span>}
        </h2>
        <p className="muted">
          {game.headers.Date ?? 'Date inconnue'} · Résultat {game.result} · Tu joues les{' '}
          {playerColor === 'w' ? 'Blancs' : 'Noirs'}
        </p>
        {saveNotice && <p className="muted small">{saveNotice}</p>}
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            setGame(null);
            setPendingColorChoice(false);
          }}
        >
          ← Importer une autre partie
        </button>
      </div>

      {pendingColorChoice && (
        <div className="panel color-confirm">
          <p>Quelle couleur as-tu jouée dans cette partie ?</p>
          <div className="color-choice" role="radiogroup" aria-label="Couleur jouée">
            <button type="button" className="primary-btn" onClick={() => confirmColorChoice('w')}>
              Blancs
            </button>
            <button type="button" className="primary-btn" onClick={() => confirmColorChoice('b')}>
              Noirs
            </button>
          </div>
        </div>
      )}

      <GameViewer game={game} playerColor={playerColor} highlightPlies={highlightPlies} />

      <AnalysisPanel game={game} playerColor={playerColor} existingReport={report} onReportReady={handleReportReady} />
    </div>
  );
}
